import type { Plan } from '@bb/automaton';
import type {
  Country,
  OrgFeatures,
  ParsedAppointment,
} from '@bb/domain/dist/types';
import type { IOnChangesObject } from 'angular';
import type { ListingLabelContextType } from 'contexts';
import type {
  AgentType,
  ActionType,
} from 'modals/AppointmentActionModal/types';
import type { OrgStatus } from '../../Router/onboarding/constants';

type ReactPropsKeyType =
  | 'pusher'
  | 'openContactDrawer'
  | 'fetchOrgUsers'
  | 'setChatVisibility'
  | 'favorites'
  | 'toggleFavorite';

export type RootScopeType = ng.IRootScopeService & {
  auth: todo;
  hasFeature: (feature: OrgFeatures) => boolean;
  isFromCountry: (country: Country) => boolean;
  pusher: todo;
  openContactDrawer: todo;
  fetchOrgUsers: todo;
  setChatVisibility: todo;
  isMobileView: boolean;
  orgStatus: OrgStatus;
  currentPlan: Plan | null;
  favorites: Set<string>;
  toggleFavorite: (listingId: string) => Promise<void>;
  openFeedbackModal: () => void;
  openAppointmentActionModal: (opts: {
    agent: AgentType;
    action: ActionType;
    appointment: ParsedAppointment;
    onConfirm?: () => void;
  }) => void;
  isDoneWelcomeFlow?: boolean;
} & Pick<
    ListingLabelContextType,
    'labelOverrideMap' | 'getAvailability' | 'getLabel'
  >;

function DefaultController(
  $scope: ng.IScope & { $parent: ng.IScope & { $ctrl: todo } },
  $rootScope: RootScopeType,
  $window: ng.IWindowService,
) {
  if ($window.location.pathname === '/external/') return;

  const {
    reactAuth,
    labelOverrideMap,
    getAvailability,
    getLabel,
    pusher,
    openContactDrawer,
    fetchOrgUsers,
    setChatVisibility,
    isMobileView,
    orgStatus,
    currentPlan,
    favorites,
    toggleFavorite,
    openFeedbackModal,
    openAppointmentActionModal,
    isDoneWelcomeFlow,
  } = $scope.$parent.$ctrl;
  // dont change the reference to $rootScope.auth on every page, for angularjs
  Object.assign($rootScope.auth, reactAuth);
  $rootScope.hasFeature = feature =>
    Boolean($rootScope.auth?.assumingUserId) ||
    Boolean($rootScope.auth?.isAssumedNotBB) ||
    $rootScope.auth?.org?.features.includes(feature);
  $rootScope.isFromCountry = (country: Country) =>
    country === $rootScope.auth?.org?.country;
  $rootScope.labelOverrideMap = labelOverrideMap;
  $rootScope.getAvailability = getAvailability;
  $rootScope.getLabel = getLabel;
  $rootScope.pusher = pusher;
  $rootScope.openContactDrawer = openContactDrawer;
  $rootScope.fetchOrgUsers = fetchOrgUsers;
  $rootScope.setChatVisibility = setChatVisibility;
  $rootScope.isMobileView = isMobileView;
  $rootScope.orgStatus = orgStatus;
  $rootScope.currentPlan = currentPlan;
  $rootScope.favorites = favorites;
  $rootScope.toggleFavorite = toggleFavorite;
  $rootScope.openFeedbackModal = openFeedbackModal;
  $rootScope.openAppointmentActionModal = openAppointmentActionModal;
  $rootScope.isDoneWelcomeFlow = isDoneWelcomeFlow;

  $scope.$parent.$ctrl.$onChanges = (changesObj: IOnChangesObject) => {
    Object.entries(changesObj).forEach(([key, value]) => {
      if (key === 'reactAuth') {
        Object.assign($rootScope.auth, value.currentValue);
      } else if (key === 'labelOverrideMap') {
        Object.assign($rootScope.labelOverrideMap, value.currentValue);
      } else {
        $rootScope[key as ReactPropsKeyType] = value.currentValue;
      }
    });
  };
}

DefaultController.$inject = ['$scope', '$rootScope', '$window'];

export default DefaultController;
